import { Archive, ArchiveRestore, Copy, MoreHorizontal, Pencil, Play, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/datetime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CUSTOMERS } from "@/lib/customers";
import { CustomerAvatar } from "./AdminHeader";

export const QuizCard = ({ quiz, t, onHost, onEdit, onDuplicate, onDelete, onArchive, hosting }) => (
  <div data-testid={`quiz-card-${quiz.id}`} className="group rounded-xl border border-border bg-card p-5 flex flex-col gap-4 hover:border-primary/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-bold text-lg truncate" data-testid="quiz-card-title">{quiz.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-1">{quiz.description || "\u00a0"}</p>
      </div>
      <span className="text-[10px] font-bold uppercase rounded px-2 py-1 bg-secondary tracking-wider">{quiz.language}</span>
    </div>
    {!quiz.mine && <div className="text-xs text-muted-foreground -mt-2" data-testid={`quiz-shared-by-${quiz.id}`}>{t("owner")}: {quiz.owner_name || "—"}</div>}
    <div className="flex -space-x-3">
      {quiz.customer_types.slice(0, 8).map((ct, i) => <CustomerAvatar key={i} src={CUSTOMERS[ct].portrait} />)}
      {quiz.customer_types.length === 0 && <div className="h-11" />}
    </div>
    <div className="flex items-center gap-4 text-sm text-muted-foreground">
      <span>{t("customers_label")}: {quiz.customer_count}</span>
      <span>{t("questions_label")}: {quiz.question_count}</span>
      <span className="w-3.5 h-3.5 rounded-sm ml-auto ring-1 ring-white/20" style={{ background: quiz.shirt_color }} title={t("shirt_color")} />
    </div>
    <div className="flex gap-2 mt-auto">
      <Button data-testid={`quiz-host-button-${quiz.id}`} onClick={onHost} disabled={hosting} className="font-bold flex-1">
        {hosting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />}{t("host")}
      </Button>
      {quiz.can_edit && <Button data-testid={`quiz-edit-button-${quiz.id}`} variant="secondary" onClick={onEdit}><Pencil className="w-4 h-4 mr-1.5" />{t("edit")}</Button>}
      {(quiz.can_copy || quiz.is_owner) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={`quiz-menu-button-${quiz.id}`} variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {quiz.can_copy && <DropdownMenuItem data-testid={`quiz-duplicate-button-${quiz.id}`} onClick={onDuplicate}><Copy className="w-4 h-4 mr-2" />{t("duplicate")}</DropdownMenuItem>}
            {quiz.is_owner && onArchive && (
              <DropdownMenuItem data-testid={`quiz-archive-button-${quiz.id}`} onClick={onArchive}>
                {quiz.archived ? <ArchiveRestore className="w-4 h-4 mr-2" /> : <Archive className="w-4 h-4 mr-2" />}{quiz.archived ? t("unarchive") : t("archive")}
              </DropdownMenuItem>
            )}
            {quiz.is_owner && <DropdownMenuItem data-testid={`quiz-delete-button-${quiz.id}`} onClick={onDelete} className="text-rose-400"><Trash2 className="w-4 h-4 mr-2" />{t("delete")}</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  </div>
);

export const GamesTable = ({ games, t, lang, onOpen, onResults, onDelete }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    {games.length === 0 ? (
      <div className="p-6 text-sm text-muted-foreground">{t("no_games")}</div>
    ) : (
      <table className="w-full text-sm" data-testid="recent-games-table">
        <thead className="text-muted-foreground text-left">
          <tr className="border-b border-border">
            <th className="px-5 py-3 font-semibold">{t("quiz_title")}</th><th className="px-5 py-3 font-semibold">PIN</th>
            <th className="px-5 py-3 font-semibold">{t("date")}</th><th className="px-5 py-3 font-semibold">{t("players_joined")}</th>
            <th className="px-5 py-3 font-semibold">{t("winner")}</th><th className="px-5 py-3 font-semibold">{t("status")}</th><th />
          </tr>
        </thead>
        <tbody>
          {games.map((g) => (
            <tr key={g.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/40">
              <td className="px-5 py-3 font-semibold">{g.title}</td>
              <td className="px-5 py-3 font-mono-jb">{g.pin}</td>
              <td className="px-5 py-3 text-muted-foreground">{fmtDateTime(g.created_at, lang)}</td>
              <td className="px-5 py-3">{g.player_count}</td>
              <td className="px-5 py-3">{g.top[0] ? `${g.top[0].name} (${g.top[0].score})` : "—"}</td>
              <td className="px-5 py-3">
                <span className={`text-xs font-bold px-2 py-1 rounded ${g.ended ? "bg-secondary text-muted-foreground" : "bg-emerald-500/15 text-emerald-400"}`}>
                  {g.ended ? t("finished") : t("live")}
                </span>
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                <Button size="sm" variant="secondary" data-testid={`game-results-button-${g.id}`} onClick={() => onResults(g.id)}>{t("view_results")}</Button>
                {!g.ended && <Button size="sm" className="ml-2" data-testid={`game-open-button-${g.id}`} onClick={() => onOpen(g.id)}>{t("open")}</Button>}
                <Button size="icon" variant="ghost" className="ml-2 text-rose-400 hover:text-rose-300" data-testid={`game-delete-button-${g.id}`} title={t("delete_game")} onClick={() => onDelete(g)}><Trash2 className="w-4 h-4" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
